// The contract's header, as a form the user can correct.
//
// A confirmation arrives with the Contract number blank, the plant spelt a way the site
// does not know, or the basis missing altogether. Before this the review could only
// list those as blockers; now each one is a field the user fills in or a record they
// pick, and the plan rebuilds as they do.
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
    CommodityModel,
    ContractDate,
    CounterpartyModel,
    Currency,
    GetCustomerDataResponse,
    LocationModel,
    Unit,
} from '../../../../Generated/Commodity-Management-Api';
import { StoneXAutocomplete, StoneXContractMonthDropdown, StoneXSelect, StoneXTextField } from '../../../StoneXMui';
import { EfpContractFields } from './EfpContractParser';
import { EfpResolution } from './EfpContractResolver';
import { EfpImpliedBasis } from './EfpUnits';
import { EfpEditableField, EfpEntityKind } from './useEfpContract';

export interface EfpContractFieldsFormProps {
    /** Current values - what was parsed with the user's corrections applied. */
    fields: EfpContractFields;
    /** What the confirmation said, so a field the user had to supply can be marked. */
    parsedFields: EfpContractFields;
    /** Set when the basis shown was worked out from the net price rather than stated. */
    impliedBasis?: EfpImpliedBasis;
    resolution: EfpResolution;
    customerData: GetCustomerDataResponse;
    onChangeField: <K extends EfpEditableField>(key: K, value: EfpContractFields[K] | undefined) => void;
    onChooseEntity: (kind: EfpEntityKind, id: number | null) => void;
    disabled?: boolean;
}

const NOT_ON_CONTRACT = 'Not on the contract';
const UNIT_OPTIONS = Object.values(Unit).filter(unit => unit !== Unit.None);

/** Both parts present, or nothing - the month dropdown reports partial dates while typing. */
function completeContractDate(date: ContractDate | null | undefined): ContractDate | undefined {
    if (!date?.month || !date?.year || date.year < 1000) return undefined;
    return { month: date.month, year: date.year };
}

function sameContractDate(a: ContractDate | undefined, b: ContractDate | undefined): boolean {
    return a?.month === b?.month && a?.year === b?.year;
}

/**
 * A number input's text, kept separately from the number it represents so that "4." and
 * "-" survive typing. The committed value only moves when the text is a real number.
 */
function useNumberDraft(value: number | undefined, commit: (next: number | undefined) => void) {
    const [draft, setDraft] = useState(value === undefined ? '' : String(value));

    useEffect(() => {
        const draftNumber = draft.trim() === '' ? undefined : Number(draft.replace(/,/g, ''));
        // Only resync when the value changed underneath the text, not when the text is
        // just a different spelling of the same number.
        if (draftNumber !== value && !(Number.isNaN(draftNumber) && value === undefined)) {
            setDraft(value === undefined ? '' : String(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const invalid = draft.trim() !== '' && Number.isNaN(Number(draft.replace(/,/g, '')));

    function onUpdate(text: string) {
        setDraft(text);
        const trimmed = text.trim();
        if (trimmed === '') {
            commit(undefined);
            return;
        }
        const next = Number(trimmed.replace(/,/g, ''));
        if (Number.isFinite(next)) commit(next);
    }

    return { draft, invalid, onUpdate };
}

export default function EfpContractFieldsForm(props: EfpContractFieldsFormProps) {
    const { fields, parsedFields, impliedBasis, resolution, customerData, onChangeField, onChooseEntity, disabled } = props;

    const commodities = customerData.commodities ?? [];
    const counterparties = customerData.counterparties ?? [];
    const locations = customerData.locations ?? [];

    const volume = useNumberDraft(fields.volume, next => onChangeField('volume', next));
    const boardPrice = useNumberDraft(fields.boardPrice, next => onChangeField('boardPrice', next));
    const basisPrice = useNumberDraft(fields.basisPrice, next => onChangeField('basisPrice', next));

    // The month dropdown reports on mount and on every keystroke, so only real changes
    // are passed up - otherwise a mount would wipe the leg edits for nothing.
    function changeMonth(key: 'futuresMonth' | 'deliveryMonth', next: ContractDate | null) {
        const complete = completeContractDate(next);
        if (!sameContractDate(complete, fields[key])) onChangeField(key, complete);
    }

    function entityError(kind: 'Commodity' | 'Seller' | 'Plant', resolved: boolean, parsedName: string | undefined): string | undefined {
        if (resolved) return undefined;
        return parsedName
            ? `The contract says "${parsedName}" - pick the matching record`
            : `${kind} is not on the contract - pick one`;
    }

    function missingHint(key: keyof EfpContractFields): string | undefined {
        return parsedFields[key] === undefined ? NOT_ON_CONTRACT : undefined;
    }

    function numberFieldText(draft: { invalid: boolean }, key: 'volume' | 'boardPrice' | 'basisPrice', label: string): { error?: string; helperText?: string } {
        if (draft.invalid) return { error: 'Not a number', helperText: 'Not a number' };
        if (fields[key] === undefined) return { error: `${label} is required`, helperText: `${label} is required` };
        return { error: undefined, helperText: missingHint(key) };
    }

    const volumeText = numberFieldText(volume, 'volume', 'Quantity');
    const boardText = numberFieldText(boardPrice, 'boardPrice', 'Board price');
    const basisText = numberFieldText(basisPrice, 'basisPrice', 'Basis');

    // A blank basis is usually blank because the form only gave a net price. When that
    // net can be reconciled with the board price the basis is implied and filled in; until
    // then, the net is shown so the user can see what there is to work from.
    const impliedHint = impliedBasis
        ? `Implied by the contract: net ${impliedBasis.netPrice} less board ${impliedBasis.boardPrice}, per ${impliedBasis.unit}`
        : undefined;

    if (impliedBasis && !basisPrice.invalid) {
        basisText.error = undefined;
        basisText.helperText = impliedHint;
    } else if (fields.basisPrice === undefined && parsedFields.netPrice !== undefined && !basisPrice.invalid) {
        const net = `${parsedFields.netPrice}${parsedFields.netPriceUnit ? ` per ${parsedFields.netPriceUnit}` : ''}`;
        basisText.error = fields.boardPriceUnit
            ? `Basis is required - the contract only gives a net price of ${net}`
            : `Basis is required - choose what the board price is per and it will be worked out from the net price of ${net}`;
        basisText.helperText = basisText.error;
    }

    // A price unit the parser could not settle is the one case where a required field is
    // blank on purpose: the confirmation quoted two units and a bare number.
    function priceUnitError(key: 'boardPriceUnit' | 'basisPriceUnit'): string | undefined {
        if (fields[key]) return undefined;
        return parsedFields[key] === undefined && parsedFields.unit !== undefined
            ? 'The contract does not say what this price is per - choose one'
            : 'Required';
    }

    const perUnit = (unit: Unit | undefined) => (unit ? ` per ${unit}` : ' (unit unknown)');
    const sameUnits = fields.boardPriceUnit !== undefined && fields.boardPriceUnit === fields.basisPriceUnit;
    const netPrice = sameUnits && fields.boardPrice !== undefined && fields.basisPrice !== undefined
        ? Number((fields.boardPrice + fields.basisPrice).toFixed(6))
        : undefined;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5 }}>
                <StoneXAutocomplete<CommodityModel>
                    label="Commodity"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    options={commodities}
                    getOptionLabel={commodity => commodity.commodityName}
                    isOptionEqualToValue={(a, b) => a.commodityId === b.commodityId}
                    value={resolution.commodity ?? null}
                    onUpdate={commodity => onChooseEntity('commodity', commodity?.commodityId ?? null)}
                    error={entityError('Commodity', !!resolution.commodity, parsedFields.commodityName)}
                />

                <StoneXAutocomplete<CounterpartyModel>
                    label="Seller"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    options={counterparties}
                    getOptionLabel={counterparty => counterparty.counterpartyName}
                    isOptionEqualToValue={(a, b) => a.counterpartyId === b.counterpartyId}
                    value={(resolution.counterparty as CounterpartyModel | undefined) ?? null}
                    onUpdate={counterparty => onChooseEntity('counterparty', counterparty?.counterpartyId ?? null)}
                    error={entityError('Seller', !!resolution.counterparty, parsedFields.sellerName)}
                />

                <StoneXAutocomplete<LocationModel>
                    label="Plant"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    options={locations}
                    getOptionLabel={location => location.locationName}
                    isOptionEqualToValue={(a, b) => a.locationId === b.locationId}
                    value={resolution.location ?? null}
                    onUpdate={location => onChooseEntity('location', location?.locationId ?? null)}
                    error={entityError('Plant', !!resolution.location, parsedFields.plantName)
                        ?? (resolution.location && !resolution.parentLocation
                            ? 'This location has no parent, so there is no hedge to transfer from'
                            : undefined)}
                    helperText={resolution.parentLocation
                        ? `Hedge held at ${resolution.parentLocation.locationName}`
                        : undefined}
                />

                <StoneXContractMonthDropdown
                    label="Futures Month"
                    fullWidth
                    disabled={disabled}
                    value={fields.futuresMonth ?? null}
                    onChange={next => changeMonth('futuresMonth', next)}
                    error={fields.futuresMonth ? undefined : 'Futures month is required'}
                />

                <StoneXContractMonthDropdown
                    label="Delivery Month"
                    fullWidth
                    disabled={disabled}
                    value={fields.deliveryMonth ?? null}
                    onChange={next => changeMonth('deliveryMonth', next)}
                    error={fields.deliveryMonth ? undefined : 'Delivery month is required'}
                />

                <StoneXTextField
                    label="Quantity"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    type="volume"
                    value={volume.draft}
                    onUpdate={volume.onUpdate}
                    error={volumeText.error}
                    helperText={volumeText.helperText}
                />

                <StoneXSelect<Unit>
                    label="Unit"
                    fullWidth
                    isControlled
                    disabled={disabled}
                    options={UNIT_OPTIONS}
                    getId={unit => unit}
                    getOptionLabel={unit => unit}
                    value={fields.unit ?? null}
                    onChange={unit => onChangeField('unit', unit ?? undefined)}
                    error={fields.unit ? undefined : 'Unit is required'}
                    helperText={missingHint('unit')}
                />

                <StoneXTextField
                    label="Board Price"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    type="price"
                    value={boardPrice.draft}
                    onUpdate={boardPrice.onUpdate}
                    error={boardText.error}
                    helperText={boardText.helperText}
                />

                <StoneXSelect<Unit>
                    label="Board Price Per"
                    fullWidth
                    isControlled
                    disabled={disabled}
                    options={UNIT_OPTIONS}
                    getId={unit => unit}
                    getOptionLabel={unit => unit}
                    value={fields.boardPriceUnit ?? null}
                    onChange={unit => onChangeField('boardPriceUnit', unit ?? undefined)}
                    error={priceUnitError('boardPriceUnit')}
                    helperText={fields.boardPriceUnit ? missingHint('boardPriceUnit') : undefined}
                />

                <StoneXTextField
                    label="Basis"
                    showRequired
                    fullWidth
                    disabled={disabled}
                    type="price"
                    value={basisPrice.draft}
                    onUpdate={basisPrice.onUpdate}
                    error={basisText.error}
                    helperText={basisText.helperText}
                />

                <StoneXSelect<Unit>
                    label="Basis Per"
                    fullWidth
                    isControlled
                    disabled={disabled}
                    options={UNIT_OPTIONS}
                    getId={unit => unit}
                    getOptionLabel={unit => unit}
                    value={fields.basisPriceUnit ?? null}
                    onChange={unit => onChangeField('basisPriceUnit', unit ?? undefined)}
                    error={priceUnitError('basisPriceUnit')}
                    helperText={impliedBasis ? 'Implied by the contract' : fields.basisPriceUnit ? missingHint('basisPriceUnit') : undefined}
                />

                <StoneXSelect<Currency>
                    label="Currency"
                    fullWidth
                    isControlled
                    disabled={disabled}
                    options={Object.values(Currency)}
                    getId={currency => currency}
                    getOptionLabel={currency => currency}
                    value={fields.currency ?? null}
                    onChange={currency => onChangeField('currency', currency ?? undefined)}
                    helperText={fields.currency ? missingHint('currency') : 'Defaults to USD'}
                />

                <StoneXTextField
                    label="Contract Number"
                    fullWidth
                    disabled={disabled}
                    value={fields.contractNumber ?? ''}
                    onUpdate={text => onChangeField('contractNumber', text.trim() === '' ? undefined : text)}
                    helperText={missingHint('contractNumber')}
                />

                <StoneXTextField
                    label="Contract Date"
                    fullWidth
                    disabled={disabled}
                    type="date"
                    value={fields.contractDate ?? ''}
                    onUpdate={text => onChangeField('contractDate', text === '' ? undefined : text)}
                    helperText={fields.contractDate ? missingHint('contractDate') : 'Defaults to today'}
                />
            </Box>

            <Typography variant="body2" color="text.secondary">
                {sameUnits
                    ? `Board ${fields.boardPrice ?? '-'} + basis ${fields.basisPrice ?? '-'} = net ${netPrice ?? '-'}${perUnit(fields.boardPriceUnit)}`
                    : `Board ${fields.boardPrice ?? '-'}${perUnit(fields.boardPriceUnit)} + basis ${fields.basisPrice ?? '-'}${perUnit(fields.basisPriceUnit)}`
                        + (fields.unit ? `; both are restated per ${fields.unit} before posting` : '')}
            </Typography>
        </Box>
    );
}
